import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { ReportingRequest, InvoicingTimeseriesResponse, ReportingComparison, InvoiceTypeKey, FlagTypeKey } from '../../types/reporting';

interface TimeseriesInput extends ReportingRequest {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    vendorNames?: string[];
    invoiceTypes?: InvoiceTypeKey[];
    locationIds?: string[];
    flagTypes?: FlagTypeKey[];
    flagMatch?: 'any' | 'exact';
}

const getInvoicingTimeseries = async (
    sessionId: string,
    input: TimeseriesInput
): Promise<InvoicingTimeseriesResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/invoicing/timeseries`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

export const useInvoicingTimeseries = (
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'biweekly' | 'month' = 'week',
    locationId?: string,
    preset?: string,
    comparison?: ReportingComparison,
    enabled = true,
    vendorNames?: string[],
    invoiceTypes?: InvoiceTypeKey[],
    locationIds?: string[],
    flagTypes?: FlagTypeKey[],
    flagMatch?: 'any' | 'exact',
) => {
    const sessionContext = useContext(SessionContext);
    const scopedLocationIds = locationIds?.length ? locationIds : undefined;

    return useQuery({
        queryKey: ['reporting', 'invoicing', 'timeseries', startDate, endDate, granularity, locationId, scopedLocationIds, preset, comparison, vendorNames, invoiceTypes, flagTypes, flagMatch],
        queryFn: () => getInvoicingTimeseries(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            granularity,
            locationId: scopedLocationIds ? undefined : locationId,
            locationIds: scopedLocationIds,
            preset,
            comparison,
            includeComparison: !!comparison,
            vendorNames: vendorNames?.length ? vendorNames : undefined,
            invoiceTypes: invoiceTypes?.length ? invoiceTypes : undefined,
            flagTypes: flagTypes?.length ? flagTypes : undefined,
            flagMatch: flagTypes?.length && flagMatch === 'exact' ? 'exact' : undefined,
        }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
